// hooks/market/useMarketCategories.ts
//
// Regroupe tout ce qui touche aux catégories du Market :
//
// - useCategories()             : charge les 7 catégories actives (à plat,
//   aucune n'a de parent_id en pratique) pour peupler un sélecteur —
//   paramètres produit (Picker) et filtre de recherche par catégorie.
// - useProductsByCategory(...)  : produits publiés d'UNE catégorie donnée,
//   paginé, avec une recherche par nom optionnelle combinable en un seul
//   appel (ex: "Mode" + "chemise").
// - useUpdateProductCategory()  : changer la catégorie d'un produit
//   existant. Ne réinvente rien — appelle simplement 'update-product'
//   avec { category_id }, exactement comme useUpdateProduct.ts, mais
//   exposé ici avec un état de chargement dédié pour ne pas mélanger ce
//   changement ponctuel avec une sauvegarde de fiche produit complète.
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'get-categories', { access_token }
//   POST {API_BASE_URL}/market   action: 'get-products-by-category', { access_token, category_id, name?, limit?, offset? }
//   POST {API_BASE_URL}/market   action: 'update-product', { access_token, product_id, category_id }
// (voir backend/handleMarket.ts → getCategories / getProductsByCategory / updateProduct)

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';
import type { MarketFeedProduct } from '@/hooks/market/useMarketFeed';

export interface MarketCategory {
  id: string;
  parent_id: string | null;
  name: string;
  slug: string;
  icon_name: string | null;
  description: string | null;
  sort_order: number;
}

// ─── useCategories ────────────────────────────────────────────────────────

export function useCategories() {
  const [categories, setCategories] = useState<MarketCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-categories',
          access_token: token,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger les catégories');
        return;
      }

      setCategories(data.categories || []);
    } catch (err) {
      console.error('[useCategories]', err);
      setError('network_error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return { categories, loading, error, refresh: fetchCategories };
}

// ─── useProductsByCategory ────────────────────────────────────────────────

const PAGE_SIZE = 20;

export function useProductsByCategory(categoryId: string | undefined, name?: string) {
  const [products, setProducts]   = useState<MarketFeedProduct[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [hasMore, setHasMore]     = useState(false);
  const [offset, setOffset]       = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback(async (targetOffset: number, replace: boolean) => {
    if (!categoryId) return;

    if (replace) setLoading(true); else setLoadingMore(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-products-by-category',
          access_token: token,
          category_id:  categoryId,
          name:         name?.trim() || undefined,
          limit:        PAGE_SIZE,
          offset:       targetOffset,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger les produits');
        return;
      }

      setProducts(prev => replace ? (data.products || []) : [...prev, ...(data.products || [])]);
      setOffset(data.next_offset ?? targetOffset);
      setHasMore(!!data.has_more);
    } catch (err) {
      console.error('[useProductsByCategory]', err);
      setError('network_error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [categoryId, name]);

  // Recharge depuis le début à chaque changement de catégorie ou de terme
  // de recherche — évite de mélanger des résultats de filtres différents.
  useEffect(() => {
    setProducts([]);
    setOffset(0);
    setHasMore(false);
    fetchPage(0, true);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    fetchPage(offset, false);
  }, [fetchPage, offset, hasMore, loadingMore]);

  const refresh = useCallback(() => {
    fetchPage(0, true);
  }, [fetchPage]);

  return { products, loading, loadingMore, error, hasMore, loadMore, refresh };
}

// ─── useUpdateProductCategory ─────────────────────────────────────────────
// Changement de catégorie isolé, avec son propre état de chargement — pour
// un écran/menu dédié (ex: appui long sur une carte produit → "Changer de
// catégorie") sans dépendre de l'état saving/error de useUpdateProduct,
// qui couvre lui toute la fiche produit.

export function useUpdateProductCategory() {
  const [updating, setUpdating] = useState(false);
  const [error, setError]       = useState<string | null>(null);

  const updateProductCategory = useCallback(async (
    productId: string,
    categoryId: string
  ): Promise<boolean> => {
    setUpdating(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return false;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'update-product',
          access_token: token,
          product_id:   productId,
          category_id:  categoryId,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de changer la catégorie du produit');
        return false;
      }

      return true;
    } catch (err) {
      console.error('[useUpdateProductCategory]', err);
      setError('network_error');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  return { updateProductCategory, updating, error };
    }
